import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { SectionCard } from "@/components/common/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import { useAuth } from "@/context/AuthContext";
import {
  Calculator, Scale, Sigma, Award, FlaskConical, GitBranch, History as HistoryIcon,
  Plus, CheckCircle2, RotateCcw, AlertCircle, Palette, Lock,
} from "lucide-react";

const hasRole = (u, ...r) => !!u && r.includes(u.role);

function VersionBadge({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-300",
    draft: "bg-amber-100 text-amber-800 border-amber-300",
    inactive: "bg-slate-200 text-slate-700 border-slate-300",
  };
  return (
    <Badge className={`${map[status] || map.inactive} uppercase tracking-wider text-[10px] font-semibold border`} data-testid={`version-status-${status}`}>
      {status}
    </Badge>
  );
}

function GradePreview({ grade, color }) {
  return (
    <span
      className="inline-flex items-center justify-center min-w-[42px] h-10 px-3 rounded-md font-heading text-lg font-black text-white shadow-sm"
      style={{ backgroundColor: color || "#64748B" }}
      data-testid={`grade-preview-${grade}`}
    >
      {grade}
    </span>
  );
}

// ----------------------------- Tab 1: Audit Formula -----------------------------
function AuditFormulaTab({ active }) {
  if (!active) return null;
  const rules = active.rules || [];
  return (
    <div className="space-y-4">
      <SectionCard
        title={`Formula: ${active.formula_group_name || "SAMS Standard Audit Formula"}`}
        subtitle={`Version v${active.version_number} · ${active.description}`}
        actions={<VersionBadge status={active.status} />}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Status</div>
            <div className="mt-1 font-heading font-bold text-slate-900">{active.status}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Effective</div>
            <div className="mt-1 text-slate-800 text-sm">{(active.effective_date || "—").slice(0, 10)}</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Immutable</div>
            <div className="mt-1 text-slate-800 text-sm">{active.is_used ? "Yes (used in audits)" : "No"}</div>
          </div>
        </div>

        <div className="space-y-4">
          {rules.map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 p-4 bg-white" data-testid={`rule-${r.formula_key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700">#{r.sort_order}</span>
                <span className="font-heading font-bold text-slate-900">{r.formula_key.replace(/_/g, " ")}</span>
              </div>
              <div className="my-3 overflow-x-auto"><BlockMath math={r.formula_expression} /></div>
              <p className="text-sm text-slate-600">{r.description}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ----------------------------- Tab 2: Weight Formula -----------------------------
function WeightFormulaTab({ active, canEdit, onSaved }) {
  const [weights, setWeights] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setWeights((active?.weights || []).map((w) => ({ ...w }))); setDirty(false); }, [active]);

  const total = useMemo(() => weights.reduce((a, w) => a + Number(w.weight_percentage || 0), 0), [weights]);
  const totalOk = Math.abs(total - 100) <= 0.02;
  const editLocked = active?.is_used || active?.status !== "active";

  const changeWeight = (idx, val) => {
    const next = [...weights];
    next[idx] = { ...next[idx], weight_percentage: Number(val) };
    setWeights(next); setDirty(true);
  };

  const save = async () => {
    if (!totalOk) return toast.error(`Total weight must equal 100% (currently ${total.toFixed(2)}%)`);
    setSaving(true);
    try {
      await api.put("/formula/weights", {
        weights: weights.map((w) => ({
          audit_category_id: w.audit_category_id,
          weight_percentage: Number(w.weight_percentage),
        })),
      });
      toast.success("Weight matrix saved");
      onSaved && onSaved();
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard
      title="Weight Formula"
      subtitle="Weighted Score = Category Percentage × Category Weight / 100"
      actions={
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${totalOk ? "text-emerald-700" : "text-red-600"}`} data-testid="weight-total">
            Total: {total.toFixed(2)}%
          </span>
          {canEdit && !editLocked && (
            <Button onClick={save} disabled={!dirty || !totalOk || saving} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="weight-save-btn">
              {saving ? "Saving..." : "Save Weights"}
            </Button>
          )}
        </div>
      }
    >
      <div className="mb-4 p-4 rounded-md border border-slate-200 bg-slate-50">
        <BlockMath math={"\\text{Weighted Score} = \\frac{\\text{Category Percentage} \\times \\text{Category Weight}}{100}"} />
      </div>

      {editLocked && (
        <div className="mb-3 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <Lock size={12} /> This version is {active?.is_used ? "immutable (already used in audits)" : "not active"}. Create a new version to edit weights.
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Audit Category</th>
              <th className="text-right p-3 w-40">Weight (%)</th>
            </tr>
          </thead>
          <tbody>
            {weights.map((w, i) => (
              <tr key={w.id || w.audit_category_id} className="border-t border-slate-100">
                <td className="p-3 font-mono text-slate-500">{i + 1}</td>
                <td className="p-3 font-semibold text-slate-800">{w.category_name}</td>
                <td className="p-3">
                  <Input
                    type="number" min="0" max="100" step="0.01"
                    disabled={!canEdit || editLocked}
                    value={w.weight_percentage}
                    onChange={(e) => changeWeight(i, e.target.value)}
                    className="text-right font-mono h-9"
                    data-testid={`weight-input-${i}`}
                  />
                </td>
              </tr>
            ))}
            <tr className="bg-slate-50 border-t border-slate-200">
              <td colSpan={2} className="p-3 font-bold text-slate-800 text-right">Total</td>
              <td className="p-3 text-right font-mono font-bold" style={{ color: totalOk ? "#059669" : "#DC2626" }}>{total.toFixed(2)}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ----------------------------- Tab 3: Final Score Formula -----------------------------
function FinalScoreTab({ active }) {
  const weights = active?.weights || [];
  return (
    <SectionCard title="Final Score Formula" subtitle="Sum of every category's weighted score">
      <div className="mb-4 p-4 rounded-md border border-slate-200 bg-slate-50">
        <BlockMath math={"\\text{Final Score} = \\sum_{c=1}^{n} \\text{Weighted Score}_c"} />
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Weight</th>
              <th className="text-left p-3">Contribution Formula</th>
            </tr>
          </thead>
          <tbody>
            {weights.map((w, i) => (
              <tr key={w.id} className="border-t border-slate-100">
                <td className="p-3 font-mono text-slate-500">{i + 1}</td>
                <td className="p-3 font-semibold text-slate-800">{w.category_name}</td>
                <td className="p-3 text-right font-mono">{Number(w.weight_percentage).toFixed(2)}%</td>
                <td className="p-3">
                  <InlineMath math={`\\text{Pct}_{${i + 1}} \\times ${Number(w.weight_percentage).toFixed(2)} / 100`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ----------------------------- Tab 4: Grade Formula -----------------------------
function GradeFormulaTab({ active, canEdit, onSaved }) {
  const [grades, setGrades] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setGrades((active?.grades || []).map((g) => ({ ...g }))); setDirty(false); }, [active]);

  const editLocked = active?.is_used || active?.status !== "active";

  const change = (i, field, val) => {
    const next = [...grades];
    next[i] = { ...next[i], [field]: field === "min_score" || field === "max_score" ? Number(val) : val };
    setGrades(next); setDirty(true);
  };
  const addRow = () => { setGrades([...grades, { grade: "?", min_score: 0, max_score: 0, description: "", color: "#64748B" }]); setDirty(true); };
  const removeRow = (i) => { setGrades(grades.filter((_, x) => x !== i)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/formula/grades", { grades: grades.map((g) => ({
        grade: g.grade, min_score: Number(g.min_score), max_score: Number(g.max_score),
        description: g.description, color: g.color,
      })) });
      toast.success("Grade matrix saved");
      onSaved && onSaved(); setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard
      title="Grade Formula"
      subtitle="Grade bands are looked up from the DB (no hardcoded IF statements)."
      actions={canEdit && !editLocked && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={addRow} data-testid="grade-add-btn"><Plus size={14} className="mr-1" /> Add band</Button>
          <Button onClick={save} disabled={!dirty || saving} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="grade-save-btn">
            {saving ? "Saving..." : "Save Grades"}
          </Button>
        </div>
      )}
    >
      {editLocked && (
        <div className="mb-3 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <Lock size={12} /> This version is {active?.is_used ? "immutable (already used in audits)" : "not active"}. Create a new version to edit grades.
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3 w-24">Grade</th>
              <th className="text-right p-3 w-32">Min Score</th>
              <th className="text-right p-3 w-32">Max Score</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-40">Color</th>
              <th className="text-center p-3 w-20">Preview</th>
              {canEdit && !editLocked && <th className="p-3 w-16"></th>}
            </tr>
          </thead>
          <tbody>
            {grades.map((g, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-2"><Input value={g.grade} disabled={!canEdit || editLocked} onChange={(e) => change(i, "grade", e.target.value)} className="h-9 font-bold uppercase text-center" data-testid={`grade-code-${i}`} /></td>
                <td className="p-2"><Input type="number" step="0.01" disabled={!canEdit || editLocked} value={g.min_score} onChange={(e) => change(i, "min_score", e.target.value)} className="h-9 text-right font-mono" /></td>
                <td className="p-2"><Input type="number" step="0.01" disabled={!canEdit || editLocked} value={g.max_score} onChange={(e) => change(i, "max_score", e.target.value)} className="h-9 text-right font-mono" /></td>
                <td className="p-2"><Input disabled={!canEdit || editLocked} value={g.description || ""} onChange={(e) => change(i, "description", e.target.value)} className="h-9" /></td>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    <input type="color" disabled={!canEdit || editLocked} value={g.color || "#64748B"} onChange={(e) => change(i, "color", e.target.value)} className="h-9 w-12 border border-slate-200 rounded cursor-pointer disabled:cursor-not-allowed" data-testid={`grade-color-${i}`} />
                    <span className="font-mono text-xs text-slate-500">{g.color}</span>
                  </div>
                </td>
                <td className="p-2 text-center"><GradePreview grade={g.grade} color={g.color} /></td>
                {canEdit && !editLocked && (
                  <td className="p-2 text-center">
                    <button onClick={() => removeRow(i)} className="text-xs text-red-600 hover:underline" data-testid={`grade-remove-${i}`}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ----------------------------- Tab 5: Formula Simulator -----------------------------
function SimulatorTab({ active }) {
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!active) return;
    const weightMap = Object.fromEntries((active.weights || []).map((w) => [w.audit_category_id, w]));
    api.get("/categories").then(({ data }) => {
      const seed = data.map((c, i) => {
        const w = weightMap[c.id]?.weight_percentage ?? 0;
        const qc = c.question_count || 5;
        return {
          audit_category_id: c.id,
          category_name: c.name,
          question_count: qc,
          max_score_per_question: 5,
          actual_score: qc * 4, // demo default (score 4)
          category_weight: Number(w) || 0,
        };
      });
      setRows(seed);
    });
  }, [active]);

  const total_weight = useMemo(() => rows.reduce((a, r) => a + Number(r.category_weight || 0), 0), [rows]);
  const weightOk = Math.abs(total_weight - 100) <= 0.02;

  const change = (i, field, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: Number(val) };
    setRows(next);
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/formula/simulate", { categories: rows });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const perCategoryDetail = (r) => {
    const max = r.question_count * r.max_score_per_question;
    const pct = max ? (r.actual_score / max) * 100 : 0;
    const ws = (pct * r.category_weight) / 100;
    return { max, pct, ws };
  };

  return (
    <SectionCard
      title="Formula Simulator"
      subtitle="Simulate a full audit across all categories. Step-by-step calculation with live totals."
      actions={
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${weightOk ? "text-emerald-700" : "text-red-600"}`} data-testid="sim-total-weight">
            Total Weight: {total_weight.toFixed(2)}%
          </span>
          <Button onClick={run} disabled={busy || !weightOk} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="sim-run-btn">
            {busy ? "Calculating..." : "Run Simulation"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 mb-4">
        {rows.map((r, i) => {
          const d = perCategoryDetail(r);
          return (
            <div key={r.audit_category_id} className="rounded-md border border-slate-200 p-4" data-testid={`sim-cat-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="font-heading font-bold text-slate-900">{i + 1}. {r.category_name}</div>
                <div className="text-xs text-slate-500">Weight in this audit: <b>{Number(r.category_weight).toFixed(2)}%</b></div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[11px] uppercase text-slate-500 font-semibold">Questions</Label>
                  <Input type="number" min="1" value={r.question_count} onChange={(e) => change(i, "question_count", e.target.value)} className="mt-1 h-9 font-mono" data-testid={`sim-q-${i}`} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase text-slate-500 font-semibold">Max Score / Q</Label>
                  <Input type="number" min="1" value={r.max_score_per_question} onChange={(e) => change(i, "max_score_per_question", e.target.value)} className="mt-1 h-9 font-mono" data-testid={`sim-max-${i}`} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase text-slate-500 font-semibold">Actual Score</Label>
                  <Input type="number" min="0" step="0.01" value={r.actual_score} onChange={(e) => change(i, "actual_score", e.target.value)} className="mt-1 h-9 font-mono" data-testid={`sim-actual-${i}`} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase text-slate-500 font-semibold">Weight (%)</Label>
                  <Input type="number" min="0" max="100" step="0.01" value={r.category_weight} onChange={(e) => change(i, "category_weight", e.target.value)} className="mt-1 h-9 font-mono" data-testid={`sim-weight-${i}`} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">1. Max Category Score</div>
                  <div className="mt-1 font-mono font-bold text-slate-900">{d.max}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5"><InlineMath math={`${r.question_count} \\times ${r.max_score_per_question}`} /></div>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">2. Category Percentage</div>
                  <div className="mt-1 font-mono font-bold text-slate-900">{d.pct.toFixed(2)}%</div>
                  <div className="text-[10px] text-slate-500 mt-0.5"><InlineMath math={`\\frac{${r.actual_score}}{${d.max}} \\times 100`} /></div>
                </div>
                <div className="rounded-md bg-sams-primary/5 border border-sams-primary/20 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">3. Weighted Score</div>
                  <div className="mt-1 font-mono font-bold text-sams-primary">{d.ws.toFixed(2)}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5"><InlineMath math={`\\frac{${d.pct.toFixed(2)} \\times ${Number(r.category_weight).toFixed(2)}}{100}`} /></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {result && (
        <div className="mt-4 rounded-md border-2 p-6" style={{ borderColor: result.grade_color || "#3B82F6", backgroundColor: (result.grade_color || "#3B82F6") + "10" }} data-testid="sim-result">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Simulator Result</div>
              <div className="mt-1 font-heading text-4xl font-black text-slate-900" data-testid="sim-final-score">{result.final_score.toFixed(2)}%</div>
              <div className="text-sm text-slate-600 mt-1">Total weight: {result.total_weight.toFixed(2)}% {result.weight_valid ? "✓" : "⚠ not 100%"}</div>
            </div>
            <div className="flex items-center gap-4">
              <GradePreview grade={result.grade || "-"} color={result.grade_color} />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Grade</div>
                <div className="font-heading text-2xl font-black text-slate-900" data-testid="sim-grade">{result.grade || "—"}</div>
                <div className="text-xs text-slate-600">{result.grade_description}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ----------------------------- Tab 6: Versions -----------------------------
function VersionsTab({ versions, canManage, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!desc.trim()) return toast.error("Description is required");
    setSaving(true);
    try {
      await api.post("/formula/versions", { description: desc });
      toast.success("New draft version created (cloned from current active)");
      setCreating(false); setDesc(""); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const activate = async (vid) => {
    if (!window.confirm("Activate this version? The current active version will be deactivated.")) return;
    try {
      await api.put(`/formula/versions/${vid}/activate`);
      toast.success("Version activated");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SectionCard
      title="Formula Versions"
      subtitle="Only one version can be active at a time. Versions become immutable once used in an audit."
      actions={canManage && (
        <Button onClick={() => setCreating(true)} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="version-new-btn">
          <Plus size={14} className="mr-1" /> New Version
        </Button>
      )}
    >
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">Version</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Created By</th>
              <th className="text-left p-3">Created</th>
              <th className="text-left p-3">Effective</th>
              <th className="text-left p-3">Expired</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Immutable</th>
              {canManage && <th className="p-3"></th>}
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} className="border-t border-slate-100" data-testid={`version-row-${v.version_number}`}>
                <td className="p-3 font-mono font-bold text-slate-900">v{v.version_number}</td>
                <td className="p-3">{v.description}</td>
                <td className="p-3">{v.created_by_name || v.created_by}</td>
                <td className="p-3 text-xs text-slate-500">{(v.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-xs text-slate-500">{(v.effective_date || "—").slice(0, 10)}</td>
                <td className="p-3 text-xs text-slate-500">{(v.expired_date || "—").slice(0, 10)}</td>
                <td className="p-3"><VersionBadge status={v.status} /></td>
                <td className="p-3">{v.is_used ? <Lock size={14} className="text-slate-600" /> : <span className="text-slate-400">—</span>}</td>
                {canManage && (
                  <td className="p-3">
                    {v.status !== "active" && (
                      <Button size="sm" variant="outline" onClick={() => activate(v.id)} data-testid={`activate-${v.version_number}`}>
                        <CheckCircle2 size={12} className="mr-1" /> Activate
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Description</Label>
            <Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. 2026-Q3 revised weight structure" data-testid="version-desc" />
            <p className="text-xs text-slate-500">A new draft will be created by cloning rules, grades and weights from the current active version.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="version-create-btn">
              {saving ? "Creating..." : "Create Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

// ----------------------------- Tab 7: History -----------------------------
function HistoryTab({ canRollback, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/formula/history").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const rollback = async (vid) => {
    const reason = window.prompt("Rollback reason:");
    if (reason === null) return;
    try {
      await api.post(`/formula/rollback/${vid}`, { reason });
      toast.success("Rollback complete");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading...</div>;

  return (
    <SectionCard title="Formula History" subtitle="Every change to the formula engine is logged and rollback-able.">
      <div className="relative pl-6">
        <div className="absolute left-2 top-0 bottom-0 w-px bg-slate-200" />
        <div className="space-y-4">
          {items.map((h) => (
            <div key={h.id} className="relative" data-testid={`history-${h.id}`}>
              <div className="absolute -left-[18px] top-1 h-3 w-3 rounded-full bg-sams-primary ring-4 ring-white" />
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-100 text-slate-800 border border-slate-300 text-[10px]">{h.change_type}</Badge>
                    {h.version_number != null && <span className="text-xs text-slate-500">v{h.version_number}</span>}
                  </div>
                  <div className="text-xs text-slate-500">{(h.changed_at || "").slice(0, 16).replace("T", " ")} · {h.changed_by_name || h.changed_by}</div>
                </div>
                {h.reason && <div className="mt-2 text-sm text-slate-700">{h.reason}</div>}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Old</div>
                    <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(h.old_value, null, 2)}</pre>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">New</div>
                    <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(h.new_value, null, 2)}</pre>
                  </div>
                </div>
                {canRollback && h.can_rollback !== false && h.formula_version_id && (
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => rollback(h.formula_version_id)} data-testid={`rollback-${h.id}`}>
                      <RotateCcw size={12} className="mr-1" /> Rollback to this version
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {!items.length && <div className="text-sm text-slate-500">No history yet.</div>}
        </div>
      </div>
    </SectionCard>
  );
}

// ----------------------------- Page -----------------------------
export default function FormulaEnginePage() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = hasRole(user, "system_admin");
  const isSdAdmin = hasRole(user, "sd_admin") || isSuperAdmin;

  const load = async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([
        api.get("/formula/active").then((r) => r.data),
        api.get("/formula/versions").then((r) => r.data),
      ]);
      setActive(a); setVersions(v);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-sm text-slate-500 p-6">Loading Formula Engine...</div>;

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">System Parameter › Formula Engine</div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Formula Engine</h1>
        <p className="text-sm text-slate-600 mt-1">
          Data-driven scoring engine. Every formula, weight, and grade band lives in the database — no hardcoded math. Changing the formula does not affect historical audits.
        </p>
      </div>

      <Tabs defaultValue="audit">
        <TabsList className="bg-white border border-slate-200 rounded-md p-1 flex-wrap h-auto">
          <TabsTrigger value="audit" data-testid="tab-audit-formula" className="gap-1"><Calculator size={13} /> Audit Formula</TabsTrigger>
          <TabsTrigger value="weight" data-testid="tab-weight-formula" className="gap-1"><Scale size={13} /> Weight Formula</TabsTrigger>
          <TabsTrigger value="final" data-testid="tab-final-formula" className="gap-1"><Sigma size={13} /> Final Score</TabsTrigger>
          <TabsTrigger value="grade" data-testid="tab-grade-formula" className="gap-1"><Award size={13} /> Grade Formula</TabsTrigger>
          <TabsTrigger value="sim" data-testid="tab-simulator" className="gap-1"><FlaskConical size={13} /> Simulator</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions" className="gap-1"><GitBranch size={13} /> Versions</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="gap-1"><HistoryIcon size={13} /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="audit" className="mt-4"><AuditFormulaTab active={active} /></TabsContent>
        <TabsContent value="weight" className="mt-4"><WeightFormulaTab active={active} canEdit={isSdAdmin} onSaved={load} /></TabsContent>
        <TabsContent value="final" className="mt-4"><FinalScoreTab active={active} /></TabsContent>
        <TabsContent value="grade" className="mt-4"><GradeFormulaTab active={active} canEdit={isSuperAdmin} onSaved={load} /></TabsContent>
        <TabsContent value="sim" className="mt-4"><SimulatorTab active={active} /></TabsContent>
        <TabsContent value="versions" className="mt-4"><VersionsTab versions={versions} canManage={isSuperAdmin} onChanged={load} /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab canRollback={isSuperAdmin} onChanged={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
